import React from "react";
import { useOnParticipantPage } from "../../hooks/useOnPage";
import { BlankButton, GreenButton, PinkButton } from "../common/CongressButton";
import { useNavigate, useLocation } from "react-router-dom";
import { Trans } from "@lingui/macro";
import { useParticipantFromUrl } from "../../hooks/useParticipantFromUrl";
import { useEventFromUrl } from "../../hooks/useEventFromUrl";
import { useEditParticipantContext } from "../../context/EditParticipantContext";

export const ParticipantUpdateActions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const registrationId = useParticipantFromUrl();
  const congressId = useEventFromUrl();
  const isParticipantSummaryPage = useOnParticipantPage();
  const isEditPage = location.pathname.includes("/edit");

  const editContext = useEditParticipantContext();
  const isBackEnabled = isEditPage && editContext?.mode !== "preview";

  const goToEdit = React.useCallback(() => {
    if (congressId && registrationId) {
      // Korábbi (akár másik résztvevőhöz tartozó) módosítás eldobása,
      // hogy a szerkesztő mindig friss adatokkal induljon
      editContext?.setModifiedData(null);
      editContext?.setMode("edit");
      navigate(
        `/authenticated/registrations/${congressId}/participant/${registrationId}/edit`
      );
    }
  }, [navigate, registrationId, congressId, editContext]);

  const goBack = React.useCallback(() => {
    // Reset the context before navigating back
    if (editContext) {
      editContext.reset();
    }
    if (isParticipantSummaryPage && congressId) {
      // A résztvevő adatairól mindig a résztvevők listájára lépünk vissza
      navigate(`/authenticated/registrations/${congressId}`);
    } else if (isEditPage && congressId && registrationId) {
      // A szerkesztőből a résztvevő adataira lépünk vissza
      navigate(
        `/authenticated/registrations/${congressId}/participant/${registrationId}`
      );
    } else {
      navigate(-1);
    }
  }, [
    navigate,
    editContext,
    isParticipantSummaryPage,
    isEditPage,
    congressId,
    registrationId,
  ]);

  const cancelPreview = React.useCallback(() => {
    if (editContext) {
      // Csak a módot változtatjuk, az initialValues automatikusan frissül
      // mert az EditParticipant.page.tsx-ben a useMemo figyeli a modifiedData-t
      editContext.setMode("edit");
    }
  }, [editContext]);

  const renderEditPageButtons = React.useCallback(() => {
    if (isEditPage && editContext) {
      const { mode, onPreview, onUpdate, reset, isUpdating } = editContext;
      if (mode === "edit") {
        return (
          <>
            <PinkButton onClick={onPreview} testId="preview-changes-button">
              <Trans>Preview</Trans>
            </PinkButton>
            <BlankButton onClick={reset} testId="reset-participant-button">
              <Trans>Reset</Trans>
            </BlankButton>
          </>
        );
      } else if (mode === "preview") {
        return (
          <>
            <PinkButton
              onClick={onUpdate}
              testId="update-participant-button"
              isLoading={isUpdating}
              loadingText="Updating..."
            >
              <Trans>Update</Trans>
            </PinkButton>
            <GreenButton
              onClick={cancelPreview}
              testId="preview-cancel-button"
              disabled={isUpdating}
            >
              <Trans>Cancel</Trans>
            </GreenButton>
          </>
        );
      }

      return <></>;
    }

    return <></>;
  }, [isEditPage, editContext, cancelPreview]);

  // On ParticipantSummaryPage: show Edit button
  if (isParticipantSummaryPage) {
    return (
      <>
        <PinkButton onClick={goToEdit} testId="edit-participant-button">
          <Trans>Edit</Trans>
        </PinkButton>

        <GreenButton onClick={goBack} testId="page-back-button">
          <Trans>Back</Trans>
        </GreenButton>
      </>
    );
  }

  return (
    <>
      {isParticipantSummaryPage && (
        <PinkButton onClick={goToEdit} testId="edit-participant-button">
          <Trans>Edit</Trans>
        </PinkButton>
      )}

      {renderEditPageButtons()}

      {isBackEnabled && (
        <GreenButton onClick={goBack} testId="page-back-button">
          <Trans>Back</Trans>
        </GreenButton>
      )}
    </>
  );
};
